import { ICommand } from '../../../core/commands/ICommand';
import type { NodeChangeRecord } from '../../../core/commands/base/BaseNodeCommand';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import type { Node } from '../../../core/models/scene';
import { useDiagramStore } from '../../../stores/diagramStore';
import { createNodeId, type NodeUpdateChange } from '../../../core/data-profiles/scenarios/ScenarioDelta';
import type { CommandDeltaContribution, IScenarioRecordable } from '../../../core/data-profiles/scenarios/ScenarioRecordable';

export interface UpdateFlowchartNodeParams {
  nodeId: string;
  changes: Partial<Node>;
}

export class UpdateFlowchartNodeCommand implements ICommand, IScenarioRecordable {
  id: string;
  name = 'Update Flowchart Node';
  description?: string;
  timestamp: number;
  readonly isScenarioRecordable = true as const;

  private params: UpdateFlowchartNodeParams;
  private changeRecord?: NodeChangeRecord;
  private mergedOldState?: Partial<Node>;
  private mergedNewState?: Partial<Node>;

  constructor(params: UpdateFlowchartNodeParams) {
    this.id = `update-flowchart-node-${Date.now()}`;
    this.timestamp = Date.now();
    this.params = params;
    this.description = `Update node ${params.nodeId}`;
  }

  execute(): void {
    const mm = getNodeMutationManager();
    this.changeRecord = mm.applyNodeChange(this.params.nodeId, this.params.changes);
    if (!this.mergedOldState) {
      this.mergedOldState = this.changeRecord.oldState;
    }
    this.mergedNewState = this.changeRecord.newState;

    this.mirrorNodeStateToStore(this.changeRecord.newState);
  }

  undo(): void {
    if (!this.mergedOldState || !this.mergedNewState) return;
    const mm = getNodeMutationManager();
    mm.revertNodeChange({
      nodeId: this.params.nodeId,
      oldState: this.mergedOldState,
      newState: this.mergedNewState,
    });
    this.mirrorNodeStateToStore(this.mergedOldState);
  }

  redo(): void {
    this.execute();
  }

  private mirrorNodeStateToStore(state: Partial<Node>): void {
    // Mirror into Zustand so TanStack/Electric persistence and immediate
    // product assertions see the same state as the mutation manager.
    try {
      const { updateNode } = useDiagramStore.getState();
      updateNode(this.params.nodeId, state);
    } catch {
      // Best-effort mirror; command mutation manager remains the source of truth.
    }
  }

  private getCurrentNode(): Node | undefined {
    return useDiagramStore.getState().nodes.find((n) => n.id === this.params.nodeId);
  }

  private getNodeValue(node: Node | undefined, key: string): unknown {
    if (!node) return undefined;
    const nodeAny = node as unknown as Record<string, unknown>;
    const attrs = (nodeAny.attrs ?? {}) as Record<string, unknown>;
    return nodeAny[key] ?? attrs[key];
  }

  private getNextValue(key: string): unknown {
    const changesAny = this.params.changes as Record<string, unknown>;
    const attrs = (changesAny.attrs ?? {}) as Record<string, unknown>;
    return changesAny[key] ?? attrs[key];
  }

  private buildUpdates(): NodeUpdateChange[] {
    const node = this.getCurrentNode();
    const updates: NodeUpdateChange[] = [];

    const currentName = this.getNodeValue(node, 'label') ?? this.getNodeValue(node, 'text');
    const nextName = this.getNextValue('label') ?? this.getNextValue('text');
    if (nextName !== undefined && nextName !== currentName) {
      updates.push({
        field: 'name',
        oldValue: (currentName as string) ?? '',
        newValue: nextName as string,
      });
    }

    const currentDescription = this.getNodeValue(node, 'description');
    const nextDescription = this.getNextValue('description');
    if (nextDescription !== undefined && nextDescription !== currentDescription) {
      updates.push({
        field: 'description',
        oldValue: currentDescription as string | undefined,
        newValue: nextDescription as string | undefined,
      });
    }

    const currentOwner = this.getNodeValue(node, 'owner');
    const nextOwner = this.getNextValue('owner');
    if (nextOwner !== undefined && nextOwner !== currentOwner) {
      updates.push({
        field: 'owner',
        oldValue: currentOwner as string | undefined,
        newValue: nextOwner as string | undefined,
      });
    }

    const currentStatus = this.getNodeValue(node, 'status');
    const nextStatus = this.getNextValue('status');
    if (nextStatus !== undefined && nextStatus !== currentStatus) {
      updates.push({
        field: 'status',
        oldValue: currentStatus as string | undefined,
        newValue: nextStatus as string | undefined,
      });
    }

    return updates;
  }

  toScenarioDelta(): CommandDeltaContribution {
    const updates = this.buildUpdates();

    if (updates.length === 0) {
      return {};
    }

    return {
      updates: updates.map((change) => ({
        nodeId: createNodeId(this.params.nodeId),
        change,
      })),
    };
  }

  getUndoDelta(): CommandDeltaContribution {
    const updates = this.buildUpdates();

    if (updates.length === 0) {
      return {};
    }

    const undoUpdates = updates.map((change) => ({
      ...change,
      oldValue: change.newValue,
      newValue: change.oldValue,
    }));

    return {
      updates: undoUpdates.map((change) => ({
        nodeId: createNodeId(this.params.nodeId),
        change,
      })),
    };
  }

  canMergeWith(other: ICommand): boolean {
    if (!(other instanceof UpdateFlowchartNodeCommand)) return false;
    return other.params.nodeId === this.params.nodeId;
  }

  mergeWith(other: ICommand): ICommand {
    if (!(other instanceof UpdateFlowchartNodeCommand)) return this;

    const mergeNested = (a: unknown, b: unknown): Record<string, unknown> | undefined => {
      const aObj = a && typeof a === 'object' ? (a as Record<string, unknown>) : undefined;
      const bObj = b && typeof b === 'object' ? (b as Record<string, unknown>) : undefined;
      if (!aObj && !bObj) return undefined;
      return { ...(aObj || {}), ...(bObj || {}) };
    };

    const currentChanges = this.params.changes as Record<string, unknown>;
    const otherChanges = other.params.changes as Record<string, unknown>;
    const mergedAttrs = mergeNested(currentChanges.attrs, otherChanges.attrs);
    const mergedProps = mergeNested(currentChanges.props, otherChanges.props);
    const mergedStyle = mergeNested(currentChanges.style, otherChanges.style);

    const mergedChanges: Partial<Node> = {
      ...currentChanges,
      ...otherChanges,
      ...(mergedAttrs ? { attrs: mergedAttrs } : {}),
      ...(mergedProps ? { props: mergedProps } : {}),
      ...(mergedStyle ? { style: mergedStyle } : {}),
    } as Partial<Node>;

    const merged = new UpdateFlowchartNodeCommand({
      nodeId: this.params.nodeId,
      changes: mergedChanges,
    });

    merged.mergedOldState = this.mergedOldState ?? this.changeRecord?.oldState;
    merged.mergedNewState = this.mergedNewState ?? this.changeRecord?.newState;
    return merged;
  }
}
